import { Link } from 'wouter';
import { ArrowLeft, FileText, AlertTriangle, Scale, Ban, RefreshCcw, Globe, Gavel, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Terms of Service Page
 *
 * Terms and conditions for using SunSquatcher.
 * Required for app store submission and legal compliance.
 */

const LAST_UPDATED = 'January 24, 2026';
const CONTACT_EMAIL = 'legal@sunsquatcher.com';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl py-6 sm:py-8 px-4">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <Link href="/">
            <Button variant="ghost" size="sm" className="mb-3 sm:mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Terms of Service</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 sm:space-y-8 text-sm sm:text-base">
          {/* Agreement to Terms */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <FileText className="h-5 w-5 text-primary" />
              Agreement to Terms
            </h2>
            <p>
              Welcome to SunSquatcher. These Terms of Service ("Terms") govern your use of the SunSquatcher
              mobile application and web service (collectively, the "Service") operated by SunSquatcher
              ("we," "our," or "us").
            </p>
            <p>
              By accessing or using the Service, you agree to be bound by these Terms. If you disagree
              with any part of the Terms, you may not access the Service.
            </p>
          </section>

          {/* Description of Service */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Description of Service</h2>
            <p>
              SunSquatcher is an AI-powered application that predicts sunrise and sunset quality based on
              weather conditions, atmospheric data, and machine learning algorithms. The Service provides:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Sunrise and sunset quality predictions (0-100% scores)</li>
              <li>7-day forecasts for optimal viewing times</li>
              <li>Location-based recommendations</li>
              <li>Photo analysis for sky conditions</li>
              <li>Push notifications for exceptional events</li>
              <li>Interactive map exploration</li>
            </ul>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">User Accounts</h2>
            <p>
              Some features of the Service require you to create an account. When you create an account,
              you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that violate these Terms or for any
              other reason at our sole discretion.
            </p>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Scale className="h-5 w-5 text-primary" />
              Acceptable Use
            </h2>
            <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service in any way that violates applicable laws or regulations</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Use automated systems (bots, scrapers) to access the Service</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Impersonate another person or entity</li>
              <li>Use the Service for commercial purposes without our permission</li>
              <li>Reverse engineer, decompile, or disassemble the Service</li>
            </ul>
          </section>

          {/* Prohibited Content */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Ban className="h-5 w-5 text-primary" />
              Prohibited Content
            </h2>
            <p>When uploading photos or other content, you agree not to upload content that:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Is illegal, harmful, threatening, abusive, or harassing</li>
              <li>Infringes on intellectual property rights of others</li>
              <li>Contains explicit or adult material</li>
              <li>Contains personal information of others without consent</li>
              <li>Is spam, advertising, or promotional material</li>
              <li>Contains malware or malicious code</li>
            </ul>
            <p>
              We reserve the right to remove any content that violates these Terms without notice.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Intellectual Property</h2>
            <p>
              The Service and its original content (excluding user-uploaded content), features, and
              functionality are and will remain the exclusive property of SunSquatcher and its licensors.
              The Service is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              Our trademarks and trade dress may not be used in connection with any product or service
              without prior written consent.
            </p>
            <h3 className="font-medium mt-4">Your Content</h3>
            <p>
              You retain ownership of any photos and content you upload to the Service. By uploading
              content, you grant us a non-exclusive, worldwide, royalty-free license to use, process,
              and analyze your content solely for the purpose of providing the Service to you.
            </p>
          </section>

          {/* Predictions Disclaimer */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Predictions Disclaimer
            </h2>
            <p>
              <strong>Important:</strong> SunSquatcher provides predictions based on weather data and
              algorithmic analysis. These predictions are estimates and should not be relied upon for
              critical decisions. We do not guarantee the accuracy of our predictions.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Weather conditions can change rapidly and unexpectedly</li>
              <li>Local conditions may differ from predicted conditions</li>
              <li>Our algorithms are continuously improving but not perfect</li>
              <li>External factors may affect actual viewing conditions</li>
            </ul>
            <p>
              Always use your own judgment and check current conditions before traveling to view
              sunrises or sunsets.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL SUNSQUATCHER, ITS DIRECTORS,
              EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION,
              LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your access to or use of or inability to access or use the Service</li>
              <li>Any conduct or content of any third party on the Service</li>
              <li>Any content obtained from the Service</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              <li>Reliance on predictions or recommendations provided by the Service</li>
              <li>Any travel or planning decisions based on our predictions</li>
            </ul>
          </section>

          {/* Disclaimer of Warranties */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY WARRANTIES
              OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES
              OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF
              PERFORMANCE.
            </p>
            <p>
              We do not warrant that the Service will function uninterrupted, secure, or error-free,
              or that defects will be corrected.
            </p>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless SunSquatcher and its officers, directors,
              employees, contractors, agents, licensors, and suppliers from and against any claims,
              liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including
              reasonable attorneys' fees) arising out of or relating to your violation of these Terms
              or your use of the Service.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <RefreshCcw className="h-5 w-5 text-primary" />
              Termination
            </h2>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without
              prior notice or liability, for any reason, including without limitation if you breach
              these Terms.
            </p>
            <p>Upon termination:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your right to use the Service will immediately cease</li>
              <li>We may delete your account and associated data</li>
              <li>Provisions that should survive termination will remain in effect</li>
            </ul>
            <p>
              You may terminate your account at any time by deleting it through your profile settings
              or by contacting us.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Gavel className="h-5 w-5 text-primary" />
              Governing Law
            </h2>
            <p>
              These Terms shall be governed and construed in accordance with the laws of the United
              States of America, without regard to its conflict of law provisions.
            </p>
            <p>
              Our failure to enforce any right or provision of these Terms will not be considered a
              waiver of those rights. If any provision of these Terms is held to be invalid or
              unenforceable by a court, the remaining provisions will remain in effect.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Dispute Resolution</h2>
            <p>
              Any dispute arising from or relating to the subject matter of these Terms shall be
              finally settled by arbitration, using the English language, administered by the
              American Arbitration Association under its Consumer Arbitration Rules.
            </p>
            <p>
              For any dispute not subject to arbitration, you agree to submit to the personal and
              exclusive jurisdiction of the state and federal courts located in the United States.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Globe className="h-5 w-5 text-primary" />
              Changes to Terms
            </h2>
            <p>
              We reserve the right to modify or replace these Terms at any time at our sole discretion.
              If a revision is material, we will provide at least 30 days' notice prior to any new
              terms taking effect.
            </p>
            <p>
              What constitutes a material change will be determined at our sole discretion. By
              continuing to access or use our Service after those revisions become effective, you
              agree to be bound by the revised terms.
            </p>
          </section>

          {/* Entire Agreement */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between
              you and SunSquatcher regarding the use of the Service, superseding any prior agreements
              between you and us relating to your use of the Service.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Mail className="h-5 w-5 text-primary" />
              Contact Us
            </h2>
            <p>If you have any questions about these Terms, please contact us:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>By email: {CONTACT_EMAIL}</li>
              <li>Through the in-app feedback feature</li>
            </ul>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-border">
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <Link href="/privacy">
              <Button variant="outline">Privacy Policy</Button>
            </Link>
            <Link href="/">
              <Button variant="ghost">Back to Home</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
